import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import SubscriptionForm from "../utils/SubscriptionForm";
import SEO from "../utils/SEO";
import { getQuizIcon } from "./quizIcons";
import QuizEvidence from "./QuizEvidence";
import { trackQuizStart, trackQuizComplete, trackQuizAnswer } from "../../utils/analytics";
import "../../styles/Quiz.css";

const DOSHA_EVIDENCE_NOTE =
  "Ayurveda's prakriti (constitution) framework is an ancient system that classifies mind-body types by the balance of vata, pitta, and kapha. It's a tool for self-understanding, not a medical diagnosis — but modern researchers have studied whether these constitutional types map to measurable biological and phenotypic differences.";

const DOSHA_REFERENCES = [
  {
    label: "Ayurvedic Medicine: In Depth",
    publisher: "NCCIH / NIH",
    url: "https://www.nccih.nih.gov/health/ayurvedic-medicine-in-depth",
  },
  {
    label: "Recapitulation of Ayurveda constitution types by machine learning of phenotypic traits",
    publisher: "Journal of Ayurveda & Integrative Medicine (PMC/NIH)",
    url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5628820/",
  },
  {
    label: "Whole-genome expression and biochemical correlates of constitutional types defined in Ayurveda",
    publisher: "BMC (PMC/NIH)",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2562368/",
  },
  {
    label: "Prakriti (constitutional typology) in Ayurveda: a critical review",
    publisher: "PMC/NIH",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12631390/",
  },
  {
    label: "Reliability of self-reported constitutional (prakriti) questionnaires in Ayurveda diagnosis",
    publisher: "PMC / NIH",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5747507/",
  },
];

// The Ayurvedic Dosha quiz. Uses the same UI shell as the data-driven quiz engine
// (Quiz.js) for a consistent, premium look — intro, progress bar, option buttons,
// email gate — while keeping its own 28-question data (/data/doshas.json) and
// positional Vata/Pitta/Kapha scoring. On completion it routes to the shared,
// shareable result page (DoshaResult at /quiz/dosha/result/:type).

const TITLE = "What's your Ayurvedic dosha?";
const INTRO =
  "Discover your mind-body type — Vata, Pitta, or Kapha — and the herbs that bring you back into balance. A general wellness quiz, not a medical diagnosis.";

// doshas.json questions are prefixed like "1/28: …" — the progress bar replaces
// that now, so strip it for a cleaner look.
const cleanQuestion = (q) => String(q || "").replace(/^\s*\d+\s*\/\s*\d+\s*:\s*/, "");

const DoshaQuiz = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]); // selected option index per question
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetch("/data/doshas.json")
      .then((res) => res.json())
      .then((data) => {
        setQuestions(data);
        trackQuizStart();
      })
      .catch((err) => console.error("Failed to load quiz data:", err));
  }, []);

  const total = questions.length;
  const q = questions[current];
  const selected = answers[current];
  const isLast = total > 0 && current === total - 1;
  const Icon = getQuizIcon("dosha");

  const selectOption = (optIndex) => {
    const next = [...answers];
    next[current] = optIndex;
    setAnswers(next);
    if (q) trackQuizAnswer(current, q.options[optIndex]);
  };

  const goNext = () => {
    if (selected == null) return;
    if (isLast) setShowForm(true);
    else setCurrent((c) => c + 1);
  };

  const goBack = () => setCurrent((c) => Math.max(0, c - 1));

  const onFormSubmit = () => {
    setShowForm(false);
    // Positional scoring: option 0 → Vata, 1 → Pitta, 2 → Kapha.
    const tally = { vata: 0, pitta: 0, kapha: 0 };
    const keys = ["vata", "pitta", "kapha"];
    answers.forEach((optIndex) => {
      const key = keys[optIndex];
      if (key) tally[key] += 1;
    });
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0];
    trackQuizComplete(`dosha:${top}`);
    navigate(`/quiz/dosha/result/${top}`);
  };

  const progress =
    total > 0 ? Math.round(((current + (selected != null ? 1 : 0)) / total) * 100) : 0;

  return (
    <div className="quiz-page">
      <SEO
        title="Free Dosha Quiz - Discover Your Ayurveda Body Type | Sarv"
        description="Take our free Dosha quiz to discover your Ayurveda body type and get personalized herbal recommendations based on your unique constitution. Vata, Pitta, or Kapha?"
        canonicalPath="/quiz"
        keywords="Dosha quiz, Ayurveda body type, Vata Pitta Kapha quiz, free Ayurveda quiz, body type quiz, holistic health quiz, personalized Ayurveda, what is my Dosha"
      />

      {total === 0 || !q ? (
        <p className="quiz-intro">Loading quiz…</p>
      ) : (
        <>
          <div className="quiz-progress">
            <div className="quiz-progress__track">
              <div className="quiz-progress__fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="quiz-progress__count">
              {current + 1} / {total}
            </span>
          </div>

          {current === 0 && (
            <>
              <p className="quiz-eyebrow">
                <span className="quiz-eyebrow__icon" aria-hidden="true">
                  <Icon />
                </span>
                {TITLE}
              </p>
              <p className="quiz-intro">{INTRO}</p>
              <p className="quiz-meta">
                {total} questions · about {Math.max(2, Math.round(total * 0.2))} min · evidence-informed
              </p>
            </>
          )}

          <div className="quiz-card">
            <h1 className="quiz-question">{cleanQuestion(q.question)}</h1>
            <div className="quiz-options" role="radiogroup" aria-label={cleanQuestion(q.question)}>
              {q.options.map((opt, i) => {
                const isSel = selected === i;
                return (
                  <button
                    type="button"
                    key={i}
                    role="radio"
                    aria-checked={isSel}
                    className={`quiz-option${isSel ? " quiz-option--selected" : ""}`}
                    onClick={() => selectOption(i)}
                  >
                    <span className="quiz-option__bullet" />
                    <span>{opt}</span>
                  </button>
                );
              })}
            </div>

            <div className="quiz-nav">
              <button
                type="button"
                className="quiz-btn quiz-btn--ghost"
                onClick={goBack}
                disabled={current === 0}
              >
                Back
              </button>
              <button
                type="button"
                className="quiz-btn quiz-btn--primary"
                onClick={goNext}
                disabled={selected == null}
              >
                {isLast ? "See my results" : "Next"}
              </button>
            </div>
          </div>

          {current === 0 && (
            <QuizEvidence note={DOSHA_EVIDENCE_NOTE} references={DOSHA_REFERENCES} />
          )}

          <p className="quiz-intro" style={{ textAlign: "center", marginTop: 20 }}>
            Curious about the other quizzes? <Link to="/quizzes">Browse all wellness quizzes →</Link>
          </p>
        </>
      )}

      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        contentClassName="quiz-modal"
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <SubscriptionForm onFormSubmit={onFormSubmit} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DoshaQuiz;
